import systemConfig from '@src/config'
import axios, { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios'
import { errorMsg, handleCommonError, handleNoCommontError } from './errorHandle'
import qs from 'qs'
import { getLocalStorage } from '../storage'
import { getState } from '@src/store/common/global'

/** 后端统一响应结构。T 是 data 字段的业务类型，默认 any 以兼容历史调用。 */
export type Response<T = any> = {
	code?: number
	msg?: string
	data?: T
}

type RequestOptions = AxiosRequestConfig & {
	url: string
	body?: unknown
	loading?: boolean
	download?: boolean
}

const { apiPrefix, authKey } = systemConfig
const baseUrl = import.meta.env.VITE_BASE_API
const basePrefix = import.meta.env.MODE === 'development' ? apiPrefix : ''
const { toogleLoading } = getState()
// 拦截器
axios.interceptors.response.use(
	// 响应拦截
	(response) => {
		const config = response.config as InternalAxiosRequestConfig & { loading?: boolean }
		if (config.loading) {
			toogleLoading(false)
		}
		// 如果是文件下载（Blob 类型），直接返回整个响应对象
		if (config.responseType === 'blob') {
			return response
		}
		const data = response.data as Response<unknown> | undefined
		if (data?.code != 200) {
			handleCommonError(data, config) //异常弹窗
			// 手动抛出错误，跳转到 catch
			return Promise.reject(new Error(data?.msg ?? 'Request failed'))
		}
		// 拦截器把项目 Response 直接抛给调用方，类型上是 AxiosResponse 的"解包"
		return data as unknown as typeof response
	},
	// 错误拦截
	async (error) => {
		const requestConfig = error.config || {}

		if (requestConfig.loading) {
			toogleLoading(false)
		}
		// 请求有响应
		if (error.response) {
			const { status, data, config } = error.response
			const { message } = data
			if (status === 400 || status === 401 || status == 500) {
				handleCommonError(data, config)
				// 👇 特别处理 401：跳转登录页
				if (status === 401) {
					const userStore = await import('@src/store/common/user')
					userStore.default.getState().logout()
					window.location.href = '/login' // 或你的登录页 URL
				}
				return Promise.reject(message)
			}
			handleNoCommontError(errorMsg)
			return Promise.reject(errorMsg)
		}
		// 请求超时
		if (error.code === 'ECONNABORTED' || error.code === 408) {
			const timeoutMsg = '请求超时，请稍后再试'
			handleNoCommontError(timeoutMsg)
			return Promise.reject(timeoutMsg)
		}
		const networkErrorMsg = '您的网络出现问题，请检查网络重试'
		handleNoCommontError(networkErrorMsg)
		return Promise.reject(networkErrorMsg)
	}
)
// request
export default async function request(options: RequestOptions) {
	const { url, loading = true } = options
	delete options.url
	delete options.loading

	const Authorization = getLocalStorage(authKey)
	// console.log('Request Authorization', Authorization)
	const headers: Record<string, string> = {}
	if (Authorization) {
		headers[authKey] = `${Authorization}`
	}
	// console.log('Request headers', headers)
	const defaultOptions: AxiosRequestConfig = {
		headers: {
			...headers
		},
		timeout: 10000,
		withCredentials: true,
		validateStatus(status: number) {
			return status >= 200 && status < 300
		}
	}
	const newOptions: RequestOptions = { ...defaultOptions, ...options }

	newOptions.loading = loading
	if (newOptions.method?.toLowerCase() === 'get') {
		// GET 请求没有请求体，把 body 转成 query params（拼到 URL 上）
		const bodyParams = newOptions.data ?? newOptions.body
		if (bodyParams) {
			newOptions.params = bodyParams as AxiosRequestConfig['params']
		}
		newOptions.paramsSerializer = (params) => {
			return qs.stringify(params, { arrayFormat: 'repeat' })
		}
	} else {
		newOptions.data = newOptions.data ?? (newOptions.body as AxiosRequestConfig['data'])
	}
	if (loading) {
		toogleLoading(true)
	}
	let newUrl = (basePrefix || baseUrl) + url
	if (newOptions.download) {
		newUrl = url
		newOptions.withCredentials = false
	}
	return axios(newUrl, newOptions)
}
